import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { DiagnosticsActions } from '../../store';
import { ConnectedDiagnosticsModal } from '../DiagnosticsModal';
import * as util from '../../utils/urlParams';
import './DiagnosticsLegend.css';
import { PrimaryButton } from '../PrimaryButton';
import darkSkyIcon from './assets/darksky-icon.png';
import { InlineEditable } from '../InlineEditable';

export class DiagnosticsLegend extends Component {
  state = { isModalOpen: false, editableContent: {} };

  saveChanges = (modalLineColorObj, modalAvailableColors) => {
    const {
      location,
      navigationParams: { selectedSubCategory },
    } = this.props;
    const { search } = location;
    const filter = util.getFilterFromUrlParams(search);
    const lineColorObj = JSON.parse(window.sessionStorage.getItem('lineColorObj'));

    if (lineColorObj !== modalLineColorObj) {
      const newDataPoints = Object.keys(modalLineColorObj);

      // Push the new url
      const newSearch = util.addSearchParams(filter, newDataPoints);
      this.props.history.push(`${selectedSubCategory.url}?${newSearch}`);

      // Set the customDataPoints as well as the lineColor variables to the selected ones
      let sessionCustomDataPoints = JSON.parse(window.sessionStorage.getItem('customDataPoints')) || {};

      sessionCustomDataPoints = {
        ...sessionCustomDataPoints,
        [selectedSubCategory.id]: newDataPoints,
      };

      window.sessionStorage.setItem('customDataPoints', JSON.stringify(sessionCustomDataPoints));

      this.props.updateLineColors(modalLineColorObj, modalAvailableColors);
    }
    this.closeModal();
  };

  openModal = () => {
    this.setState((state) => ({
      ...state,
      isModalOpen: true,
    }));
  };

  closeModal = () => {
    this.setState((state) => ({
      ...state,
      isModalOpen: false,
    }));
  };

  updateEditable = (e, tag) => {
    const { value } = e.target;

    this.setState((state) => ({
      ...state,
      editableContent: {
        ...state.editableContent,
        [tag]: value,
      },
    }));
  };

  keyPressed = (e, updateApiLocation) => {
    const { key } = e;

    if (key === 'Enter') {
      this.updateDataPointName(e, updateApiLocation);
    }
  };

  updateDataPointName = (e, updateApiLocation) => {
    const {
      updateDataPointName,
      navigationParams: { selectedSubCategory },
    } = this.props;
    const { value } = e.target;

    updateDataPointName(value, updateApiLocation, selectedSubCategory.api_location);
  };

  renderLegendItems() {
    const { selectedDataPointsList, diagnosticsLineColors, isStaff, setEditingState } = this.props;
    const { editableContent } = this.state;
    let sortedSelectedDataPointsList = [...selectedDataPointsList];

    // sort selectedDataPointsList by register number, treat default_all register as 0
    sortedSelectedDataPointsList = sortedSelectedDataPointsList.sort((a, b) => {
      // set any default_all register_number's to 0
      if (a.is_default) {
        if (b.modbus_register > 0) return -1;
        if (b.modbus_register < 0) return 1;
      }
      if (b.is_default) {
        if (a.modbus_register < 0) return -1;
        if (a.modbus_register > 0) return 1;
      }
      if (a.modbus_register < b.modbus_register) return -1;
      if (a.modbus_register > b.modbus_register) return 1;
      return 0;
    });

    return (
      <div>
        {sortedSelectedDataPointsList.map((dataPoint) => {
          const { status, modbus_register, display_name, is_darksky_data, tag, update_api_location } = dataPoint;
          const dataPointName = display_name;
          const dataPointNumber = modbus_register;
          const dataTag = tag;
          const dataPointClass = 'equal-width';
          const dataPointClassWide = 'wide-column';
          const lineStyle = `line ${diagnosticsLineColors[dataTag]}`;
          const isDarkSkyData = is_darksky_data;
          const updateApiLocation = update_api_location;
          const editableText = editableContent[dataTag] ? editableContent[dataTag] : dataPointName;
          if (!diagnosticsLineColors[dataTag]) {
            return null;
          }

          return (
            <div key={dataTag} className="diagnostics-legend__item">
              <div className={dataPointClass}>
                <div className={lineStyle} />
              </div>
              <div className={dataPointClass}>{dataPointNumber}</div>
              <div className={dataPointClass}>{dataTag}</div>
              <div className={dataPointClassWide}>
                {isStaff ? (
                  <InlineEditable text={editableText} type="input" setEditingState={setEditingState}>
                    <input
                      type="text"
                      name="dataPoint"
                      value={editableText}
                      onChange={(e) => this.updateEditable(e, dataTag)}
                      onBlur={(e) => this.updateDataPointName(e, updateApiLocation)}
                      onKeyUp={(e) => this.keyPressed(e, updateApiLocation)}
                      autoFocus
                    />
                  </InlineEditable>
                ) : (
                  dataPointName
                )}
                {isDarkSkyData && (
                  <a href="https://darksky.net/poweredby/" target="_blank" rel="noopener noreferrer">
                    <img className="diagnostics-legend__dark-sky-icon" src={darkSkyIcon} alt="dark sky" />
                  </a>
                )}
              </div>
              <div className={dataPointClass}>{status}</div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const { setEditingState } = this.props;

    return (
      <React.Fragment>
        <div className="diagnostics-legend">
          <div className="diagnostics-legend__header">
            <div className="diagnostics-legend__title">Legend</div>
            <PrimaryButton icon="edit-icon" label="Add/Remove Lines" onClick={this.openModal} />
          </div>
          <div className="diagnostics__column-names">
            <div className="equal-width">LINE STYLE</div>
            <div className="equal-width">MODBUS REGISTER</div>
            <div className="equal-width">DATA TAG</div>
            <div className="wide-column">DATA SET</div>
            <div className="equal-width">STATUS</div>
          </div>
          {this.renderLegendItems()}
        </div>
        {this.state.isModalOpen ? (
          <ConnectedDiagnosticsModal
            onRequestClose={this.closeModal}
            saveChanges={this.saveChanges}
            setEditingState={setEditingState}
          />
        ) : null}
      </React.Fragment>
    );
  }
}

DiagnosticsLegend.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  history: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  location: PropTypes.object.isRequired,
  isStaff: PropTypes.bool.isRequired,
  selectedDataPointsList: PropTypes.arrayOf(
    PropTypes.shape({
      api_location: PropTypes.string,
      is_default: PropTypes.bool,
      id: PropTypes.number,
      display_name: PropTypes.string,
      modbus_device: PropTypes.number,
      modbus_register_number: PropTypes.number,
      status: PropTypes.string,
      tag: PropTypes.string,
    }),
  ),
  navigationParams: PropTypes.shape({
    selectedCategory: PropTypes.shape({
      api_location: PropTypes.string,
      url: PropTypes.string,
      name: PropTypes.string,
      image: PropTypes.string,
      sub_categories: PropTypes.arrayOf(
        PropTypes.shape({
          api_location: PropTypes.string,
          data_api_location: PropTypes.string,
          id: PropTypes.string,
          url: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
      id: PropTypes.string,
    }),
    selectedSubCategory: PropTypes.shape({
      api_location: PropTypes.string,
      data_api_location: PropTypes.string,
      id: PropTypes.string,
      url: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  updateLineColors: PropTypes.func.isRequired,
  updateDataPointName: PropTypes.func.isRequired,
  setEditingState: PropTypes.func.isRequired,
};

DiagnosticsLegend.defaultProps = {
  selectedDataPointsList: [],
  navigationParams: {},
};

export function mapStateToProps(state) {
  return {
    selectedDataPointsList: state.diagnosticsReducer.selectedDataPointsList,
    navigationParams: state.buildingReducer.navigationParams,
    diagnosticsLineColors: state.diagnosticsReducer.lineColorObj,
    isStaff: state.profileReducer.is_staff,
  };
}

const mapDispatchToProps = {
  updateLineColors: DiagnosticsActions.updateLineColors,
  updateDataPointName: DiagnosticsActions.updateDataPointName,
};

const DiagnosticsLegendWithRouter = withRouter(DiagnosticsLegend);
const ConnectedDiagnosticsLegendWithRouter = connect(mapStateToProps, mapDispatchToProps)(DiagnosticsLegendWithRouter);
export { ConnectedDiagnosticsLegendWithRouter };
